// src/services/adminService.js
import { collection, getDocs, doc, getDoc, setDoc, deleteDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../config/firebase';

const functions = getFunctions(app);

/**
 * Admin roles enum
 */
export const ADMIN_ROLES = {
  SUPER_ADMIN: 'superadmin',
  ORDER_MANAGER: 'order_manager',
  OPERATIONS_MANAGER: 'operations_manager'
};

/**
 * Role permissions mapping
 */
export const ROLE_PERMISSIONS = {
  [ADMIN_ROLES.SUPER_ADMIN]: {
    dashboard: true,
    orders: true,
    products: true,
    inventory: true,
    coupons: true,
    users: true,
    contacts: true,
    settings: true,
    canManageAdmins: true
  },
  [ADMIN_ROLES.ORDER_MANAGER]: {
    dashboard: true,
    orders: true,
    products: false,
    inventory: false,
    coupons: false,
    users: false,
    contacts: false,
    settings: false,
    canManageAdmins: false
  },
  [ADMIN_ROLES.OPERATIONS_MANAGER]: {
    dashboard: true,
    orders: true,
    products: true,
    inventory: true,
    coupons: true,
    users: true,
    contacts: false,
    settings: false,
    canManageAdmins: false
  }
};

/**
 * Get role display name
 */
export function getRoleDisplayName(role) {
  const names = {
    [ADMIN_ROLES.SUPER_ADMIN]: 'Super Admin',
    [ADMIN_ROLES.ORDER_MANAGER]: 'Order Manager',
    [ADMIN_ROLES.OPERATIONS_MANAGER]: 'Operations Manager'
  };
  return names[role] || 'Unknown';
}

/**
 * Fetch admin data with role information
 * @param {string} email - Admin email
 * @returns {Promise<Object|null>} Admin data or null
 */
export async function getAdminData(email) {
  try {
    if (!email) return null;
    
    const adminRef = doc(db, 'admin', email);
    const adminSnap = await getDoc(adminRef);
    
    if (!adminSnap.exists()) {
      return null;
    }
    
    const data = adminSnap.data();
    return {
      email,
      role: data.role || ADMIN_ROLES.SUPER_ADMIN, // Default to super admin for legacy entries
      addedBy: data.addedBy || null,
      createdAt: data.createdAt || null,
      status: data.status || 'active',
      ...data
    };
  } catch (error) {
    console.error('[AdminService] Error fetching admin data:', error);
    return null;
  }
}

/**
 * Fetch admin emails from Firestore
 * Admin emails are stored as document IDs in the 'admin' collection
 * @returns {Promise<Array<string>>} Array of admin email addresses
 */
export async function fetchAdminEmails() {
  try {
    const adminCollection = collection(db, 'admin');
    const querySnapshot = await getDocs(adminCollection);
    
    if (querySnapshot.empty) {
      console.warn('[AdminService] No admin documents found. Please create the "admin" collection in Firestore.');
      return [];
    }
    
    // Extract emails from document IDs
    const adminEmails = querySnapshot.docs.map(doc => doc.id);
    
    return adminEmails;
  } catch (error) {
    // Suppress permission errors (expected for non-admin users)
    if (!error.code?.includes('permission')) {
      console.error('[AdminService] Error fetching admin emails:', error);
    }
    return [];
  }
}

/**
 * Check if a user is an admin (with any role)
 * @param {string} email - User email to check
 * @returns {Promise<boolean>} True if user is admin
 */
export async function isAdmin(email) {
  try {
    if (!email) return false;
    const adminData = await getAdminData(email);
    return adminData !== null && adminData.status === 'active';
  } catch (error) {
    console.error('[AdminService] Error checking admin status:', error);
    return false;
  }
}

/**
 * Check if admin has specific permission
 * @param {string} email - Admin email
 * @param {string} permission - Permission to check (e.g., 'orders', 'products')
 * @returns {Promise<boolean>} True if admin has permission
 */
export async function hasPermission(email, permission) {
  try {
    const adminData = await getAdminData(email);
    if (!adminData || adminData.status !== 'active') return false;
    
    const permissions = ROLE_PERMISSIONS[adminData.role];
    return permissions ? permissions[permission] === true : false;
  } catch (error) {
    console.error('[AdminService] Error checking permission:', error);
    return false;
  }
}

/**
 * Fetch all admins
 * @returns {Promise<Array>} Array of admin objects
 */
export async function fetchAllAdmins() {
  try {
    const adminCollection = collection(db, 'admin');
    const querySnapshot = await getDocs(adminCollection);
    
    if (querySnapshot.empty) {
      console.warn('[AdminService] No admin documents found.');
      return [];
    }
    
    const admins = querySnapshot.docs.map(doc => ({
      email: doc.id,
      role: doc.data().role || ADMIN_ROLES.SUPER_ADMIN,
      addedBy: doc.data().addedBy || null,
      createdAt: doc.data().createdAt || null,
      status: doc.data().status || 'active',
      ...doc.data()
    }));
    
    return admins;
  } catch (error) {
    console.error('[AdminService] Error fetching all admins:', error);
    return [];
  }
}

/**
 * Add a new admin
 * @param {string} email - Admin email to add
 * @param {string} role - Admin role
 * @param {string} addedByEmail - Email of admin adding this user
 * @returns {Promise<Object>} Result object
 */
export async function addAdmin(email, role, addedByEmail) {
  try {
    // Validate inputs
    if (!email || !role || !addedByEmail) {
      throw new Error('Email, role, and addedBy are required');
    }
    
    // Check if caller is super admin
    const callerData = await getAdminData(addedByEmail);
    if (!callerData || callerData.role !== ADMIN_ROLES.SUPER_ADMIN) {
      throw new Error('Only super admins can add new admins');
    }
    
    // Check if admin already exists
    const existingAdmin = await getAdminData(email);
    if (existingAdmin) {
      throw new Error('This email is already an admin');
    }
    
    // Validate role
    if (!Object.values(ADMIN_ROLES).includes(role)) {
      throw new Error('Invalid role');
    }
    
    // Create admin document
    const adminRef = doc(db, 'admin', email);
    await setDoc(adminRef, {
      role,
      addedBy: addedByEmail,
      createdAt: Timestamp.now(),
      status: 'active'
    });
    
    return { success: true, message: 'Admin added successfully' };
  } catch (error) {
    console.error('[AdminService] Error adding admin:', error);
    return { success: false, message: error.message };
  }
}

/**
 * Update admin role
 * @param {string} email - Admin email to update
 * @param {string} newRole - New role
 * @param {string} updatedByEmail - Email of admin making the update
 * @returns {Promise<Object>} Result object
 */
export async function updateAdminRole(email, newRole, updatedByEmail) {
  try {
    // Check if caller is super admin
    const callerData = await getAdminData(updatedByEmail);
    if (!callerData || callerData.role !== ADMIN_ROLES.SUPER_ADMIN) {
      throw new Error('Only super admins can update admin roles');
    }
    
    // Cannot modify super admins
    const targetAdmin = await getAdminData(email);
    if (targetAdmin && targetAdmin.role === ADMIN_ROLES.SUPER_ADMIN) {
      throw new Error('Cannot modify super admin roles');
    }
    
    // Validate role
    if (!Object.values(ADMIN_ROLES).includes(newRole)) {
      throw new Error('Invalid role');
    }
    
    const adminRef = doc(db, 'admin', email);
    await updateDoc(adminRef, {
      role: newRole,
      updatedAt: Timestamp.now(),
      updatedBy: updatedByEmail
    });
    
    return { success: true, message: 'Admin role updated successfully' };
  } catch (error) {
    console.error('[AdminService] Error updating admin role:', error);
    return { success: false, message: error.message };
  }
}

/**
 * Remove admin
 * @param {string} email - Admin email to remove
 * @param {string} removedByEmail - Email of admin removing this user
 * @returns {Promise<Object>} Result object
 */
export async function removeAdmin(email, removedByEmail) {
  try {
    // Check if caller is super admin
    const callerData = await getAdminData(removedByEmail);
    if (!callerData || callerData.role !== ADMIN_ROLES.SUPER_ADMIN) {
      throw new Error('Only super admins can remove admins');
    }
    
    // Cannot remove super admins
    const targetAdmin = await getAdminData(email);
    if (targetAdmin && targetAdmin.role === ADMIN_ROLES.SUPER_ADMIN) {
      throw new Error('Cannot remove super admin');
    }
    
    // Cannot remove self
    if (email === removedByEmail) {
      throw new Error('Cannot remove yourself');
    }
    
    const adminRef = doc(db, 'admin', email);
    await deleteDoc(adminRef);
    
    return { success: true, message: 'Admin removed successfully' };
  } catch (error) {
    console.error('[AdminService] Error removing admin:', error);
    return { success: false, message: error.message };
  }
}

/**
 * Sync Firebase Auth custom admin claim from Firestore admin collection.
 * Must be called after login so Storage rules can authorize admin uploads.
 */
export async function syncAdminStorageClaim() {
  try {
    const syncClaim = httpsCallable(functions, 'syncAdminStorageClaim');
    const result = await syncClaim({});
    return result.data || { success: false, admin: false, claimsUpdated: false };
  } catch (error) {
    console.error('[AdminService] Error syncing admin claim:', error);
    return { success: false, admin: false, claimsUpdated: false };
  }
}
